<!--
                               o8o                 .
                               `"'               .o8
   .oooo.o  .ooooo.  oooo d8b oooo  oo.ooooo.  .o888oo
  d88(  "8 d88' `"Y8 `888""8P `888   888' `88b   888
  `"Y88b.  888        888      888   888   888   888
  o.  )88b 888   .o8  888      888   888   888   888 .
  8""888P' `Y8bod8P' d888b    o888o  888bod8P'   "888"
                                     888
                                    o888o
 -->

<script>

import { TweenMax as Tween, Power1 } from 'gsap'

import BaseButton from '@/components/base-button'
import LoadMoreButton from './load-more-button'
import TransitionWrapper from '@/layout/wrappers/transition-wrapper'

export default {

  name: 'List',

  components: {
    BaseButton,
    LoadMoreButton,
    TransitionWrapper
  },

  props: {
    type: {
      type: String,
      required: true,
      validator (value) {
        const isValid = value.match(/^(?:news|projects)$/)
        if (!isValid) console.warn('Validator message: List type must be "projects" or "news".')
        return isValid
      }
    },
    items: {
      type: Array,
      required: true
    },
    count: {
      type: Number,
      required: true
    }
  },

  filters: {
    formatDate: (timestamp) => {
      const date = new Date(timestamp)
      const day = date.getDate().toString().length < 2 ? '0' + date.getDate() : date.getDate()
      const month = date.getMonth().toString().length < 2 ? '0' + date.getMonth() : date.getMonth()
      const year = date.getFullYear()

      return `${day} / ${month} / ${year}`
    }
  },

  data () {
    return {
      visible: false,
      height: 0
    }
  },

  computed: {
    hasHiddenItems () {
      return this.count > this.items.length
    },

    lang () {
      return this.$route.params.lang
        ? this.$route.params.lang
        : 'ua'
    }
  },

  watch: {
    '$store.state.app.window.width': 'calculateHeight',
    height (value) {
      const duration = Math.max(0.5 * (this.items.length / 2), 0.5)

      Tween.to(this.$refs.bg, duration, {
        height: value,
        ease: Power1.easeInOut,
        force3D: true
      })
    }
  },

  methods: {
    calculateHeight () {
      this.height = this.$el.getBoundingClientRect().height
    },

    getBackgroundImage (item) {
      if (item.media && item.media.image) {
        return `background-image: url(${item.media.image.url})`
      } else {
        return `background-image: url(/public/images/project-cover.jpg)`
      }
    },

    appear (el, done) {
      if (this.$refs.noNewsMessage) {
        this.calculateHeight()

        Tween.from(this.$refs.noNewsMessage, 1, {
          y: -20,
          autoAlpha: 0,
          delay: 0.5,
          ease: Power1.easeOut
        })

        return
      }
    },

    itemEnter (el, done) {
      const stagger = el.dataset.index * 0.1
      const delay = this.visible ? 0 : 0.5

      Tween.from(el, 0.5, {
        autoAlpha: 0,
        y: -40,
        delay: stagger + delay,
        ease: Power1.easeOut,
        onComplete: done
      })

      this.calculateHeight()
    }
  }

}

</script>


<!--

  oooo                                                  .
  `888                                                .o8
   888   .oooo.   oooo    ooo  .ooooo.  oooo  oooo  .o888oo
   888  `P  )88b   `88.  .8'  d88' `88b `888  `888    888
   888   .oP"888    `88..8'   888   888  888   888    888
   888  d8(  888     `888'    888   888  888   888    888 .
  o888o `Y888""8o     .8'     `Y8bod8P'  `V88V"V8P'   "888"
                  .o..P'
                  `Y8P'
 -->


<template lang="pug">

.container

  .container__background(ref="bg")
    transition-wrapper(:appear="true")
      load-more-button.more-button(v-if="hasHiddenItems" @click="$emit('load-more-items')")

  transition-group.list(
    v-if="items.length > 0"
    appear
    tag="ul"
    :class="`is-${this.type}`"
    @enter="itemEnter"
    ref="list")

    //- Story
    li.story(v-if="type === 'news'" v-for="item, index in items", :key="index", :data-index="index")
      router-link.story-thumb(:style="getBackgroundImage(item)", :to="'/' + lang + '/news' + item.url")
      .story-content
        .story-content__info
          a.story-content__title(:url="'/' + lang + '/news' + item.url") {{ item.title || 'Story title' }}
          .story-content__date {{ item.createdAt | formatDate }}
        base-button.story-content__link(:url="'/' + lang + '/news' + item.url", :label="$t('read-article')")

    //- Project
    li.project(v-if="type === 'projects'" v-for="item, index in items", :key="index", :data-index="index")
      router-link.project-link(:to="'/' + lang + '/projects' + item.url")
        .project-thumb(:style="getBackgroundImage(item)")
        .project-content
          .project-content__title {{ item.title || 'Project title' }}
          .project-content__cat {{ item.cat }}

  .empty-list(v-else)
    .empty-list__title(ref="noNewsMessage") {{ type === 'news' ? $t('no-news') : $t('no-projects')}}

</template>


<!--
               .               oooo
             .o8               `888
   .oooo.o .o888oo oooo    ooo  888   .ooooo.   .oooo.o
  d88(  "8   888    `88.  .8'   888  d88' `88b d88(  "8
  `"Y88b.    888     `88..8'    888  888ooo888 `"Y88b.
  o.  )88b   888 .    `888'     888  888    .o o.  )88b
  8""888P'   "888"     .8'     o888o `Y8bod8P' 8""888P'
                   .o..P'
                   `Y8P'
 -->


<style lang="stylus" scoped>

.container
  position relative
  display flex
  justify-content center
  align-items center
  flex-direction column
  width 100%
  padding-top 145px
  padding-bottom 150px

  +tablet(1023px)
    padding 90px 0 120px

  +mobile()
    padding 75px 0

  &__background
    z-index 0
    position absolute
    top 0
    left 0
    display block
    width 100%
    height 0
    background-color #F9F9F9
    transform-origin center top

    & ~ *
      z-index 1

.empty-list
  display flex
  flex-direction column

  &__title
    font-weight 500
    font-size 26px
    line-height 34px
    color dark

.list
  display flex
  flex-direction column
  list-style-type none
  max-width 1138px
  width 100%
  padding 0 65px

  &.is-projects
    flex-direction row
    flex-wrap wrap

  +hd()
    max-width 1390px

  +tablet()
    padding 0 33px

  +mobile()
    display block
    max-width 450px
    padding 0 23px

.more-button
  position absolute
  right 0
  bottom -72px
  left 0
  margin auto

// Story

.story
  display flex
  justify-content space-between

  +hd()
    justify-content flex-start

  +tablet()
    padding-right 20px

  +mobile()
    flex-direction column

  & + &
    margin-top 80px
    +below(1023px)
      margin-top 60px

.story-thumb
  width 454px
  height 282px
  max-height 26vw
  background-position center
  background-repeat no-repeat
  background-size cover
  transition opacity .3s

  .story:hover &
    opacity 0.45

  +hd()
    max-width 640px
    height 400px

  +laptop()
    width 440px
    height 280px

  +tablet()
    width 50%
    height 200px

  +mobile()
    max-width 100%
    max-height 250px
    min-width 0
    height calc((100vw - 46px) * .62)

.story-content
  display flex
  flex-direction column
  justify-content space-between
  margin-left 114px
  padding-top 35px
  padding-bottom 40px
  max-width 400px
  width 100%

  +hd()
    margin-left 180px
    max-width 460px
    padding 50px 0

  +laptop()
    margin-left 80px

  +devices()
    padding 0

  +tablet()
    width 50%
    margin-left 80px

  +mobile()
    margin-top 26px
    margin-left 0

  &__info
    display flex
    flex-direction column

    +mobile()
      & + *
        margin-top 20px

  &__title
    font-weight 500
    font-size 26px
    line-height 34px
    color dark

    +hd()
      font-size 36px
      line-height 45px

    +mobile()
      font-size 17px
      line-height 23px

  &__date
    margin-top 22px
    font-size 12px
    line-height 20px
    color light

    +hd()
      font-size 16px

    +tablet()
      margin-top 8px

    +mobile()
      margin-top 17px
      font-size 11px
      line-height 19px

  &__link
    align-self flex-start

// Project

.project
  display flex

  +above(768px)
    max-width 454px
    width 46%

    &:nth-child(2n)
      margin-left auto

    &:nth-child(1n+3)
      margin-top 90px

  +hd()
    max-width 560px

  +mobile()
    & + &
      margin-top 56px

.project-link
  display flex
  flex-direction column
  width 100%

.project-thumb
  width 100%
  height 418px
  background-repeat no-repeat
  background-size cover
  background-position center
  transition opacity .3s

  +above(768px)
    max-height 39vw

  +above(1024px)
    .project-link:hover &
      opacity 0.45

  +mobile()
    height 371px
    max-height calc((100vw - 46px) * .92)

.project-content
  padding-top 43px
  +mobile()
    padding-top 26px

  &__title
    font-weight 500
    font-size 36px
    line-height 42px

    +above(1024px)
      .project-link:hover &
        text-decoration underline
        text-decoration-color orange

    +mobile()
      font-size 17px
      line-height 23px

  &__cat
    margin-top 22px
    font-size 12px
    line-height 20px
    color light
    +mobile()
      margin-top 17px
      font-size 11px
      line-height 19px

</style>

<style lang="stylus">
.h1-like
  font-size 56px
  line-height 57px
  font-weight 500
  +hd()
    font-size 76px
    line-height 76px

  +tablet()
    font-size 46px
    line-height 50px

  +mobile()
    font-size 34px
    line-height 37px

</style>



// WEBPACK FOOTER //
// list.vue?4bbfa322